import {
  Bot,
  Building2,
  ClipboardList,
  Gauge,
  KeyRound,
  ListFilter,
  MousePointerClick,
  PencilLine,
  Plug,
  Rocket,
  Rows3,
  Target,
  UserCog,
  Users,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import type { UserOrganization, UserPermissions } from "@/lib/auth";
import type { SettingsTab } from "@/lib/search-params";

// One Settings page, three scopes. The Company groups configure the Company
// the person is in: its admins (and super admins) change them; members and
// viewers read the ones the server lets them read, as before Settings held
// Global admin. "All Companies" is what used to be Global admin: Companies for
// Company managers and super admins, the rest for super admins only. "You" is
// the person's own: every signed-in person opens it. This is navigation; every
// server boundary checks the same rules on its own.

export type SettingsScope = "company" | "general" | "personal";

/**
 * Who may read a Company section, mirroring its queries' server checks:
 * "all" for every Company role, "modules" for admins and members (Viewer is
 * Media Performance only), "admins" for Company admins only.
 */
type CompanyReaders = "all" | "modules" | "admins";

export interface SettingsSectionDef {
  key: SettingsTab;
  label: string;
  icon: LucideIcon;
  scope: SettingsScope;
  /** An explanation the section needs but does not show by default. */
  hint?: string;
  /** Company sections only. */
  readers?: CompanyReaders;
}

interface GroupDef {
  /** A Company group's label follows the Company's name: "Rebil · Reporting". */
  label: string;
  scope: SettingsScope;
  sections: SettingsSectionDef[];
}

const company = (
  key: SettingsTab,
  label: string,
  icon: LucideIcon,
  readers: CompanyReaders,
  hint?: string,
): SettingsSectionDef => ({ key, label, icon, scope: "company", readers, hint });
const general = (
  key: SettingsTab,
  label: string,
  icon: LucideIcon,
  hint?: string,
): SettingsSectionDef => ({ key, label, icon, scope: "general", hint });

const GROUPS: GroupDef[] = [
  {
    label: "Reporting",
    scope: "company",
    sections: [
      company("conversions", "Conversions", Target, "admins"),
      company("manual-data", "Manual data", PencilLine, "all"),
      company("segments", "Filters and breakdowns", ListFilter, "all"),
      company("display", "Report layout", Rows3, "all"),
      company(
        "attribution",
        "Attribution",
        MousePointerClick,
        "all",
        "The conversion windows Meta and Snapchat are read under. TikTok and Google Ads use the windows set in each platform.",
      ),
      company(
        "extra-metrics",
        "Extra metrics",
        Gauge,
        "all",
        "Metrics beyond spend and conversions this Company reports. Each one turned on is a column under Columns in Media Performance › By campaign and By creative, and is in reports from chat.",
      ),
    ],
  },
  {
    label: "Company",
    scope: "company",
    sections: [
      company(
        "audit-context",
        "Business context",
        ClipboardList,
        "modules",
        "What the AI reads about the Company in chat and in every Audit. Assisted fill in the Mac app fills most of it in.",
      ),
      company(
        "budgets",
        "Budgets",
        Wallet,
        "modules",
        "Safeguards for Budget plans in Launch › Budgets and from chat.",
      ),
    ],
  },
  {
    label: "Access",
    scope: "company",
    sections: [
      company("members", "Members", Users, "admins"),
      company("connections", "Connections", Plug, "admins"),
    ],
  },
  {
    label: "All Companies",
    scope: "general",
    sections: [
      general("companies", "Companies", Building2),
      general("people", "People", UserCog),
      general(
        "logins",
        "Platform logins",
        KeyRound,
        "The logins Growth Engine reads Meta, Google, TikTok and Snapchat with. Ad accounts are assigned per Company, under Companies.",
      ),
      general("launch-ops", "Launch operations", Rocket),
    ],
  },
  {
    label: "You",
    scope: "personal",
    sections: [
      {
        key: "connected-apps",
        label: "Connected apps",
        icon: Bot,
        scope: "personal",
        hint: "Connect Claude or ChatGPT, and the Mac app if you run skills. A chat sees the Companies you can see.",
      },
    ],
  },
];

export const SETTINGS_SECTIONS: SettingsSectionDef[] = GROUPS.flatMap((group) => group.sections);

type Viewer = Pick<UserPermissions, "isSuperAdmin"> & { isBrandManager?: boolean };
type Membership = Pick<UserOrganization, "role"> | null | undefined;

/** Whether this person manages the Company: changes its settings. */
export function canManageCompany(org: Membership, viewer: Viewer): boolean {
  return Boolean(org) && (org!.role === "admin" || viewer.isSuperAdmin);
}

function canReadCompanySection(readers: CompanyReaders, org: Membership, viewer: Viewer) {
  if (!org) return false;
  if (canManageCompany(org, viewer)) return true;
  if (readers === "all") return true;
  return readers === "modules" && org.role === "member";
}

/**
 * What this person may do with one Settings section, in this Company (if any):
 * "edit", "read" (a Company section shown without manage rights) or null when
 * they may not open it. The one permission rule Settings navigation follows.
 */
export function settingsSectionAccess(
  key: SettingsTab,
  org: Membership,
  viewer: Viewer,
): "edit" | "read" | null {
  const section = SETTINGS_SECTIONS.find((item) => item.key === key);
  if (!section) return null;
  switch (section.scope) {
    case "personal":
      return "edit";
    case "general":
      if (key === "companies")
        return viewer.isSuperAdmin || Boolean(viewer.isBrandManager) ? "edit" : null;
      return viewer.isSuperAdmin ? "edit" : null;
    case "company":
      if (!canReadCompanySection(section.readers ?? "admins", org, viewer)) return null;
      // Audit context belongs to the Audit module: whoever may use it may change it.
      if (key === "audit-context") return "edit";
      return canManageCompany(org, viewer) ? "edit" : "read";
  }
}

/** Whether this person may open one Settings section, in this Company (if any). */
export function canOpenSettingsSection(key: SettingsTab, org: Membership, viewer: Viewer): boolean {
  return settingsSectionAccess(key, org, viewer) !== null;
}

export interface SettingsNavGroup {
  label: string;
  sections: SettingsSectionDef[];
}

/** The groups this person sees, in order, with empty groups left out. */
export function settingsNavGroups(
  org: Pick<UserOrganization, "role" | "name"> | null | undefined,
  viewer: Viewer,
): SettingsNavGroup[] {
  return GROUPS.map((group) => ({
    label: group.scope === "company" && org ? `${org.name} · ${group.label}` : group.label,
    sections: group.sections.filter((section) => canOpenSettingsSection(section.key, org, viewer)),
  })).filter((group) => group.sections.length > 0);
}

/**
 * The section Settings opens on: the Company's first for those who manage it,
 * else the first All Companies section they may open, else their first one.
 * Every signed-in person has at least their own sections.
 */
export function firstSettingsTab(org: Membership, viewer: Viewer): SettingsTab {
  const open = SETTINGS_SECTIONS.filter((section) =>
    canOpenSettingsSection(section.key, org, viewer),
  );
  const managing = canManageCompany(org, viewer);
  return (
    open.find(
      (section) => section.scope === "general" || (managing && section.scope === "company"),
    ) ?? open[0]!
  ).key;
}

/** The Settings section an `/admin?tab=…` link means (old Global admin tabs or new values). */
export function settingsTabFromAdminTab(tab: unknown): SettingsTab {
  switch (tab) {
    case "people":
      return "people";
    case "connections":
    case "logins":
      return "logins";
    case "launch":
    case "launch-ops":
      return "launch-ops";
    case "connected-apps":
      return "connected-apps";
    default:
      // "brands" predates the Company vocabulary; it and no tab land on Companies.
      return "companies";
  }
}
